import { useEffect, useState } from "react";
import { api, fmtErr } from "../../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { ShieldCheck, Users, Ship, Store, ClipboardList, Receipt, Ban, RotateCcw, KeyRound, Trash2, HardDrive, Database } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import AffiliatesAdmin from "../admin/AffiliatesAdmin";

const Kpi = ({ label, value, sub, icon: Icon }) => (
  <div className="border border-zinc-200 rounded-xl bg-white p-4">
    <div className="flex items-center gap-2 text-xs text-zinc-500">{Icon && <Icon className="h-3.5 w-3.5" />}{label}</div>
    <div className="text-2xl font-display mt-1">{value ?? "—"}</div>
    {sub && <div className="text-xs text-zinc-400 mt-0.5">{sub}</div>}
  </div>
);

function Overview() {
  const [data, setData] = useState(null);
  const [growth, setGrowth] = useState([]);
  useEffect(() => {
    api.get("/admin/overview").then(({ data }) => setData(data)).catch((e) => toast.error(fmtErr(e)));
    api.get("/admin/growth?days=90").then(({ data }) => setGrowth(data.series)).catch(() => {});
  }, []);
  if (!data) return <p className="text-sm text-zinc-500 mt-4">Loading…</p>;
  const inv = Object.entries(data.invoices || {});
  return (
    <div className="space-y-6 mt-4 px-4 md:px-0" data-testid="padmin-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Users" value={data.users.total} sub={`${data.users.verified} verified · ${data.users.providers} suppliers · ${data.users.disabled} disabled`} icon={Users} />
        <Kpi label="Yachts" value={data.yachts} icon={Ship} />
        <Kpi label="Suppliers (master DB)" value={data.suppliers.total} sub={`${data.suppliers.online} online · ${data.suppliers.offline} offline`} icon={Store} />
        <Kpi label="Tasks" value={data.tasks.total} sub={`${data.tasks.open} open · ${data.tasks.completed} done`} icon={ClipboardList} />
        <Kpi label="Quotes" value={Object.values(data.quotes).reduce((a, b) => a + b, 0)} sub={Object.entries(data.quotes).map(([k, v]) => `${v} ${k}`).join(" · ")} icon={Receipt} />
        <Kpi label="Inventory items" value={data.inventory_items} />
        <Kpi label="Logbook entries" value={data.logbook_entries} />
        <Kpi label="AI chat messages" value={data.ai_chat_messages} />
      </div>

      <div className="border border-zinc-200 rounded-xl bg-white p-4">
        <h3 className="font-display mb-3">Growth — last 90 days</h3>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={growth}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={30} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={28} />
              <Tooltip />
              <Area type="monotone" dataKey="signups" stroke="#0f172a" fill="#0f172a22" name="Signups" />
              <Area type="monotone" dataKey="quotes" stroke="#2563eb" fill="#2563eb22" name="Quotes" />
              <Area type="monotone" dataKey="tasks" stroke="#059669" fill="#05966922" name="Tasks" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <div className="border border-zinc-200 rounded-xl bg-white p-4">
          <h3 className="font-display mb-2">Supplier funnel</h3>
          {[["Offline (listed)", data.suppliers.offline], ["Invited (login linked)", data.suppliers.invited], ["Claimed / online", data.suppliers.claimed]].map(([l, v]) => (
            <div key={l} className="flex items-center gap-2 py-1.5 text-sm">
              <span className="w-44 text-zinc-500">{l}</span>
              <div className="flex-1 bg-zinc-100 rounded-full h-2.5"><div className="bg-ink h-2.5 rounded-full" style={{ width: `${data.suppliers.total ? (v / data.suppliers.total) * 100 : 0}%` }} /></div>
              <span className="w-8 text-right font-medium">{v}</span>
            </div>
          ))}
          <div className="text-xs text-zinc-400 mt-2">Sources: {Object.entries(data.suppliers.by_source).map(([k, v]) => `${v} ${k}`).join(" · ")}</div>
        </div>
        <div className="border border-zinc-200 rounded-xl bg-white p-4">
          <h3 className="font-display mb-2">Invoices</h3>
          {inv.length === 0 && <p className="text-sm text-zinc-500">No invoices yet.</p>}
          {inv.map(([status, v]) => (
            <div key={status} className="flex justify-between py-1 text-sm">
              <span className="capitalize text-zinc-500">{status}</span>
              <span>{v.count} · €{v.amount?.toLocaleString?.() ?? v.amount}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function UsersTab() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/users", { params: { q } }).then(({ data }) => setRows(data)).catch((e) => toast.error(fmtErr(e)));
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q]); // eslint-disable-line

  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(fmtErr(e)); } };
  return (
    <div className="mt-4 space-y-3" data-testid="padmin-users">
      <Input placeholder="Search name or email…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm" data-testid="padmin-user-search" />
      <div className="border border-zinc-200 rounded-xl bg-white divide-y divide-zinc-100 overflow-x-auto">
        {rows.map((u) => (
          <div key={u.id} className="px-4 py-3 flex flex-wrap items-center gap-2 text-sm" data-testid={`padmin-user-${u.id}`}>
            <div className="min-w-0 flex-1">
              <div className="font-medium flex items-center gap-2">
                {u.name || "—"}
                {u.disabled && <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">disabled</Badge>}
                {!u.email_verified && <Badge variant="secondary">unverified</Badge>}
                {u.account_kind === "provider" && <Badge className="bg-blue-50 text-blue-700 hover:bg-blue-50">supplier</Badge>}
              </div>
              <div className="text-xs text-zinc-500">{u.email} · {u.yachts_owned} yachts owned · {u.yacht_memberships} memberships · joined {(u.created_at || "").slice(0, 10)}</div>
            </div>
            <div className="flex gap-1.5">
              {u.disabled ? (
                <Button size="sm" variant="outline" onClick={() => act(() => api.post(`/admin/users/${u.id}/enable`), "Account re-enabled")} data-testid={`padmin-enable-${u.id}`}><RotateCcw className="h-3.5 w-3.5 mr-1" />Enable</Button>
              ) : (
                <Button size="sm" variant="outline" onClick={() => act(() => api.post(`/admin/users/${u.id}/disable`), "Account disabled")} data-testid={`padmin-disable-${u.id}`}><Ban className="h-3.5 w-3.5 mr-1" />Disable</Button>
              )}
              <Button size="sm" variant="outline" onClick={() => act(() => api.post(`/admin/users/${u.id}/reset-password`), "Reset email sent")} data-testid={`padmin-reset-${u.id}`}><KeyRound className="h-3.5 w-3.5 mr-1" />Reset</Button>
              <Button size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50"
                onClick={() => { if (window.confirm(`Delete account ${u.email}? Their login is removed and any linked supplier profile goes back to offline/unclaimed. Data records are kept. This cannot be undone.`)) act(() => api.delete(`/admin/users/${u.id}`), "Account deleted"); }}
                data-testid={`padmin-delete-${u.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        ))}
        {rows.length === 0 && <p className="p-4 text-sm text-zinc-500">No users found.</p>}
      </div>
    </div>
  );
}

function YachtsTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/yachts").then(({ data }) => setRows(data)).catch((e) => toast.error(fmtErr(e))); }, []);
  return (
    <div className="mt-4 border border-zinc-200 rounded-xl bg-white divide-y divide-zinc-100" data-testid="padmin-yachts">
      {rows.map((y) => (
        <div key={y.id} className="px-4 py-3 text-sm flex flex-wrap items-center gap-2">
          <div className="flex-1 min-w-0">
            <div className="font-medium">{y.name}</div>
            <div className="text-xs text-zinc-500">Owner: {y.owner_name || "—"} ({y.owner_email || "—"}) · created {(y.created_at || "").slice(0, 10)}</div>
          </div>
          <Badge variant="secondary">{y.members} members</Badge>
          <Badge variant="secondary">{y.open_tasks} open tasks</Badge>
        </div>
      ))}
      {rows.length === 0 && <p className="p-4 text-sm text-zinc-500">No yachts.</p>}
    </div>
  );
}

const fmtBytes = (n) => {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
};

function StorageTab() {
  const [data, setData] = useState(null);
  const [dbStats, setDbStats] = useState(null);
  const [showAllColl, setShowAllColl] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  useEffect(() => {
    api.get("/admin/storage-by-user").then(({ data }) => setData(data)).catch((e) => toast.error(fmtErr(e)));
    api.get("/admin/storage-stats").then(({ data }) => setDbStats(data)).catch(() => {});
  }, []);
  if (!data) return <p className="text-sm text-zinc-500 mt-4">Loading…</p>;
  const max = Math.max(...data.rows.map((r) => r.bytes), 1);
  const coll = dbStats?.collections || [];
  const visibleColl = showAllColl ? coll : coll.slice(0, 10);
  return (
    <div className="mt-4 space-y-3" data-testid="padmin-storage">
      {dbStats && (
        <div data-testid="padmin-db-stats">
          <div className="flex items-center justify-between gap-2 mb-2">
            <h3 className="font-display flex items-center gap-2"><Database className="h-4 w-4 text-zinc-400" />Database</h3>
            <Button
              size="sm" variant="outline" disabled={cleaning}
              onClick={async () => {
                if (!window.confirm("Delete audit-log entries older than 90 days and notifications older than 30 days? Business data is never touched.")) return;
                setCleaning(true);
                try {
                  const { data: r } = await api.post("/admin/cleanup-logs");
                  toast.success(`Cleaned up — ${r.audit_deleted} audit entries and ${r.notifications_deleted} notifications removed`);
                  const { data: s } = await api.get("/admin/storage-stats");
                  setDbStats(s);
                } catch (e) { toast.error(fmtErr(e)); }
                finally { setCleaning(false); }
              }}
              data-testid="padmin-cleanup-btn"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" />{cleaning ? "Cleaning…" : "Clean up old logs"}
            </Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Database size" value={`${dbStats.total_mb} MB`} sub={`data + indexes · ${dbStats.db_name}`} icon={Database} />
            <Kpi label="Data" value={`${dbStats.data_mb} MB`} sub={`${dbStats.storage_mb} MB on disk`} />
            <Kpi label="Indexes" value={`${dbStats.index_mb} MB`} />
            <Kpi label="Documents" value={dbStats.objects?.toLocaleString?.() ?? dbStats.objects} sub={`${dbStats.collection_count} collections`} />
          </div>
          {coll.length > 0 && (
            <div className="mt-3 border border-zinc-200 rounded-xl bg-white divide-y divide-zinc-100" data-testid="padmin-db-collections">
              {visibleColl.map((c) => (
                <div key={c.name} className="px-4 py-2 text-sm flex items-center gap-3" data-testid={`padmin-coll-${c.name}`}>
                  <span className="flex-1 min-w-0 truncate font-mono text-xs">{c.name}</span>
                  <Badge variant="secondary">{c.count.toLocaleString()} docs</Badge>
                  <span className="font-mono text-xs w-24 text-right">{c.data_mb} MB</span>
                </div>
              ))}
              {coll.length > 10 && (
                <button
                  type="button"
                  onClick={() => setShowAllColl((v) => !v)}
                  className="w-full px-4 py-2 text-xs text-zinc-500 hover:bg-zinc-50 text-left"
                  data-testid="padmin-coll-toggle"
                >
                  {showAllColl ? "Show fewer" : `Show all ${coll.length} collections`}
                </button>
              )}
            </div>
          )}
        </div>
      )}
      <h3 className="font-display pt-2 flex items-center gap-2"><HardDrive className="h-4 w-4 text-zinc-400" />File storage</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Total storage used" value={fmtBytes(data.total_bytes)} icon={HardDrive} />
        <Kpi label="Total files" value={data.total_files} />
        <Kpi label="Users with uploads" value={data.rows.length} />
      </div>
      <div className="border border-zinc-200 rounded-xl bg-white divide-y divide-zinc-100" data-testid="padmin-storage-rows">
        {data.rows.map((r) => (
          <div key={r.user_id || "unknown"} className="px-4 py-3 text-sm flex flex-wrap items-center gap-2" data-testid={`padmin-storage-${r.user_id || "unknown"}`}>
            <div className="flex-1 min-w-0">
              <div className="font-medium flex items-center gap-2">
                {r.name || r.email || r.user_id || "Unknown"}
                {r.account_kind === "provider" && <Badge className="bg-blue-50 text-blue-700 hover:bg-blue-50">supplier</Badge>}
                {!r.email && <Badge variant="secondary">no account</Badge>}
              </div>
              {r.email && <div className="text-xs text-zinc-500 truncate">{r.email}</div>}
              <div className="mt-1.5 bg-zinc-100 rounded-full h-1.5 max-w-sm">
                <div className="bg-ink h-1.5 rounded-full" style={{ width: `${(r.bytes / max) * 100}%` }} />
              </div>
            </div>
            <Badge variant="secondary">{r.files} file{r.files !== 1 ? "s" : ""}</Badge>
            <span className="font-mono text-xs w-20 text-right" data-testid={`padmin-storage-bytes-${r.user_id || "unknown"}`}>{fmtBytes(r.bytes)}</span>
          </div>
        ))}
        {data.rows.length === 0 && <p className="p-4 text-sm text-zinc-500">No files uploaded yet.</p>}
      </div>
      <p className="text-xs text-zinc-400">Counts every object in R2 storage (photos, documents, invoices, media, inventory photos) attributed to the uploading account.</p>
    </div>
  );
}

function ActivityTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/activity").then(({ data }) => setData(data)).catch((e) => toast.error(fmtErr(e))); }, []);
  if (!data) return <p className="text-sm text-zinc-500 mt-4">Loading…</p>;
  return (
    <div className="mt-4 grid md:grid-cols-2 gap-3" data-testid="padmin-activity">
      <div className="border border-zinc-200 rounded-xl bg-white p-4">
        <h3 className="font-display mb-2">Recent platform events</h3>
        <div className="space-y-2 max-h-[28rem] overflow-y-auto">
          {data.events.map((e, i) => (
            <div key={i} className="text-xs border-b border-zinc-50 pb-1.5">
              <span className="text-zinc-400">{(e.created_at || "").slice(0, 16).replace("T", " ")}</span>{" "}
              <span className="font-medium">{e.user_name || ""}</span> {e.summary || e.action}
            </div>
          ))}
          {data.events.length === 0 && <p className="text-sm text-zinc-500">No events.</p>}
        </div>
      </div>
      <div className="space-y-3">
        <div className="border border-zinc-200 rounded-xl bg-white p-4">
          <h3 className="font-display mb-2">Newest users</h3>
          {data.new_users.map((u, i) => (
            <div key={i} className="text-xs py-1 flex justify-between">
              <span>{u.name || u.email} {u.account_kind === "provider" && <Badge className="ml-1 bg-blue-50 text-blue-700 hover:bg-blue-50">supplier</Badge>}</span>
              <span className="text-zinc-400">{(u.created_at || "").slice(0, 10)}</span>
            </div>
          ))}
        </div>
        <div className="border border-zinc-200 rounded-xl bg-white p-4">
          <h3 className="font-display mb-2">Recent supplier activations</h3>
          {data.supplier_claims.length === 0 && <p className="text-sm text-zinc-500">None yet.</p>}
          {data.supplier_claims.map((c, i) => (
            <div key={i} className="text-xs py-1 flex justify-between">
              <span>{c.company_name} <span className="text-zinc-400">({c.supplier_no})</span></span>
              <span className="text-zinc-400">{(c.claimed_at || "").slice(0, 10)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function PlatformAdmin() {
  return (
    <div data-testid="platform-admin-page">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-5 w-5" />
        <h1 className="font-display text-2xl">Platform Admin</h1>
      </div>
      <Tabs defaultValue="overview" className="mt-4">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="padmin-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="users" data-testid="padmin-tab-users">Users</TabsTrigger>
          <TabsTrigger value="yachts" data-testid="padmin-tab-yachts">Yachts</TabsTrigger>
          <TabsTrigger value="activity" data-testid="padmin-tab-activity">Activity</TabsTrigger>
          <TabsTrigger value="storage" data-testid="padmin-tab-storage">Storage</TabsTrigger>
          <TabsTrigger value="affiliates" data-testid="padmin-tab-affiliates">Affiliates</TabsTrigger>
        </TabsList>
        <TabsContent value="overview"><Overview /></TabsContent>
        <TabsContent value="users"><UsersTab /></TabsContent>
        <TabsContent value="yachts"><YachtsTab /></TabsContent>
        <TabsContent value="activity"><ActivityTab /></TabsContent>
        <TabsContent value="storage"><StorageTab /></TabsContent>
        <TabsContent value="affiliates"><AffiliatesAdmin /></TabsContent>
      </Tabs>
    </div>
  );
}
